import React, { useState, useRef, useCallback, useContext } from "react";
import {
  Grid,
  Typography,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Link,
  Button as MuiButton,
} from "@mui/material";
import { Title } from "src/pages/Messaging/components/Title";
import { useTitle } from "src/hooks/useTitle";
import Button from "@jbhi-fi/lanyard-ui/components/Button/Button";
import { Add } from "@mui/icons-material";
import { ProductSearchField } from "src/pages/ProductSync/components/ProductSearchField";
import { useCountry } from "src/components/Context/CountryContext";
import { ProductRow } from "src/pages/ProductSync/components/ProductRow";
import Papa from "papaparse";
import PageContext from "src/components/Context/PageContext";
import { hasPermission } from "src/infrastructure/auth/PermissionUtils";
import { UserRoles } from "src/infrastructure/auth/UserRoles";
import { postProductSyncMappingsAsync, postProductSyncSameLocationAsync } from "src/infrastructure/http/appApiService";
import { Dialog as MuiDialog } from "@mui/material";
import { CountryRadioGroup } from "./components/CountryRadioGroup";
import { SyncOptionsSelect } from "./components/SyncOptionsSelect";

export type SyncRow = {
  fromValue: string;
  toValue: string;
  fromProduct?: any;
  toProduct?: any;
};

export const ProductSyncPage: React.FC = () => {
  useTitle("Product Synchronization");

  const [rows, setRows] = useState<SyncRow[]>(Array.from({ length: 5 }, () => ({ fromValue: "", toValue: "" })));
  const [open, setOpen] = useState(false);
  const { country } = useCountry();
  const [errorRows, setErrorRows] = useState<number[]>([]);
  const [globalError, setGlobalError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmSyncOpen, setConfirmSyncOpen] = useState(false);
  const [fromCountry, setFromCountry] = useState<"AU" | "NZ">("AU");
  const [toCountry, setToCountry] = useState<"AU" | "NZ">("NZ");
  const [selectedSyncOptions, setSelectedSyncOptions] = useState<string[]>([
    "productInfo",
    "images",
    "specsAndTracklisting",
    "extendedContent",
    "dimensions",
  ]);
  const isSameLocationSync = fromCountry === toCountry;

  const [contextData] = useContext(PageContext);
  const { userProfile } = contextData;

  const handleBulkUploadClick = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalConfirm = () => {
    setIsModalOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const parsedRows = results.data as any[];
          if (!parsedRows.length) {
            setGlobalError("CSV file is empty or invalid.");
            return;
          }

          const headingRow = parsedRows[0];
          const columns = Object.keys(headingRow);
          if (columns.length < 2) {
            setGlobalError("CSV must contain at least two columns.");
            return;
          }

          const fromCol = columns[0];
          const toCol = columns[1];
          if (fromCol === "AUSku" && toCol === "AUSku_1") {
            setFromCountry("AU");
            setToCountry("AU");
          } else if (fromCol === "NZSku" && toCol === "NZSku_1") {
            setFromCountry("NZ");
            setToCountry("NZ");
          } else if (fromCol === "AUSku" && toCol === "NZSku") {
            setFromCountry("AU");
            setToCountry("NZ");
          }
          const newRows: SyncRow[] = parsedRows.map((row) => ({
            fromValue: row[fromCol]?.trim() || "",
            toValue: row[toCol]?.trim() || "",
          }));
          setRows(newRows);
          setErrorRows([]);
          setGlobalError(null);
        },
        error: (err) => {
          setGlobalError("Failed to parse CSV file. Please check the format.");
        },
      });
    }
    e.target.value = "";
  }, []);

  const handleInputChange = (index: number, field: "from" | "to", sku: string, product?: any) => {
    const updatedRows = [...rows];
    if (field === "from") {
      updatedRows[index].fromValue = sku;
      updatedRows[index].fromProduct = product;
    } else {
      updatedRows[index].toValue = sku;
      updatedRows[index].toProduct = product;
    }
    setRows(updatedRows);
    setGlobalError(null);
  };

  const handleAddRow = () => {
    setRows([...rows, { fromValue: "", toValue: "" }]);
  };

  const clearRows = () => {
    setRows(Array.from({ length: 5 }, () => ({ fromValue: "", toValue: "" })));
    setErrorRows([]);
    setGlobalError(null);
  };

  const handleSync = async () => {
    const invalidIndexes: number[] = [];
    let hasAnyProduct = false;

    rows.forEach((row, index) => {
      const hasFrom = !!row.fromValue || !!row.fromProduct;
      const hasTo = !!row.toValue || !!row.toProduct;

      if (hasFrom || hasTo) hasAnyProduct = true;
      if ((hasFrom && !hasTo) || (!hasFrom && hasTo)) invalidIndexes.push(index);
    });

    if (!hasAnyProduct) {
      setGlobalError("You must select at least one product pair before syncing.");
      return;
    }

    if (invalidIndexes.length > 0) {
      setErrorRows(invalidIndexes);
      setGlobalError(null);
      return;
    }
    setConfirmSyncOpen(true);
  };

  const handleConfirmSync = async () => {
    setConfirmSyncOpen(false);
    try {
      if (isSameLocationSync) {
        selectedSyncOptions.length === 0 && setGlobalError("Please select at least one sync option.");
        const mappings = rows
          .filter((row) => (row.fromProduct?.sku || row.fromValue) && (row.toProduct?.sku || row.toValue))
          .map((row) => ({
            sourceSku: row.fromProduct?.sku || row.fromValue,
            targetSku: row.toProduct?.sku || row.toValue,
          }));

        await postProductSyncSameLocationAsync(
          mappings,
          userProfile.email,
          selectedSyncOptions,
          fromCountry === "AU" ? "AuToAu" : "NzToNz",
          country
        );
      } else if (fromCountry === "AU" && toCountry === "NZ") {
        const mappings = rows
          .filter((row) => (row.fromProduct?.sku || row.fromValue) && (row.toProduct?.sku || row.toValue))
          .map((row) => ({
            auSku: row.fromProduct?.sku || row.fromValue,
            nzSku: row.toProduct?.sku || row.toValue,
          }));

        await postProductSyncMappingsAsync(mappings, userProfile.email, country);
      } else if (fromCountry === "NZ" && toCountry === "AU") {
        setGlobalError("NZ to AU sync is not supported at this time.");
        return;
      } else {
        setGlobalError("Invalid country selection. Please ensure 'From' and 'To' countries are correct.");
        return;
      }
      setOpen(true);
      clearRows();
    } catch (error) {
      console.error("Sync error:", error);
      if (error?.response?.status === 400) {
        setGlobalError("The input provided is invalid. Please check and try again.");
      } else {
        setGlobalError("Sync failed. Please try again or contact support.");
      }
    }
  };

  const handleClose = () => setOpen(false);

  const renderSKUOnly = (sku: string, onClear: () => void) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: 1,
        py: 1,
        border: "1px solid #ccc",
        borderRadius: "4px",
      }}
    >
      <Typography>{sku}</Typography>
      <MuiButton onClick={onClear} size="small" color="error">
        Clear
      </MuiButton>
    </Box>
  );

  const handleCancel = () => {
    const confirmed = window.confirm("Are you sure you want to clear all SKUs?");
    if (!confirmed) return;
    clearRows();
  };

  const clearColumnInRows = (column: "from" | "to") => {
    const valueKey = column === "from" ? "fromValue" : "toValue";
    const productKey = column === "from" ? "fromProduct" : "toProduct";
    setRows((prevRows) =>
      prevRows.map((row) => ({
        ...row,
        [valueKey]: "",
        [productKey]: undefined,
      }))
    );
    setErrorRows([]);
    setGlobalError(null);
  };

  if (!hasPermission(UserRoles.ProductSync, userProfile.roles)) {
    window.location.href = "/";
    return null;
  }
  return (
    <Grid container spacing={3} paddingTop={4}>
      <Grid item xs={12}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Title>Product Synchronization</Title>
          </Grid>
          <Button onClick={handleBulkUploadClick} type="secondary" label="Bulk Upload" />
          <input type="file" ref={fileInputRef} style={{ display: "none" }} accept=".csv" onChange={handleFileUpload} />

          <Dialog open={isModalOpen} onClose={handleModalClose}>
            <DialogTitle>Bulk Upload SKUs</DialogTitle>
            <DialogContent>
              <Typography>
                Please upload a <strong>CSV</strong> file containing one column of <code>AUSku</code> and one column of{" "}
                <code>NZSku</code>.{" "}
                <Link href="/bulk_upload_productsync.csv" download>
                  Download sample CSV template here
                </Link>
                .
              </Typography>
              <Typography sx={{ mt: 2 }}>
                <strong>Please note:</strong> Bulk upload will <strong>replace</strong> the current table with new rows.
                Changes will only take effect after syncing.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button type="secondary" onClick={handleModalClose}>
                Cancel
              </Button>
              <Button type="primary" onClick={handleModalConfirm}>
                Upload
              </Button>
            </DialogActions>
          </Dialog>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Box
          sx={{
            border: "1px solid #d1d9e0",
            borderRadius: "4px",
            overflow: "hidden",
            width: "100%",
            maxWidth: "1200px",
            margin: "0 auto",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              bgcolor: "#f5f5f5",
            }}
          >
            <Box
              sx={{
                padding: "16px",
              }}
            >
              <CountryRadioGroup
                label="From"
                value={fromCountry}
                onChange={(newFromCountry) => {
                  clearColumnInRows("from");
                  setFromCountry(newFromCountry);
                }}
              />
              <Typography variant="caption">Single SKU only</Typography>
            </Box>
            <Box sx={{ padding: "16px" }}>
              <CountryRadioGroup
                label="To"
                value={toCountry}
                onChange={(newToCountry) => {
                  clearColumnInRows("to");
                  setToCountry(newToCountry);
                }}
              />
              <Typography variant="caption">Single SKU only</Typography>
            </Box>
          </Box>

          {rows.map((row, index) => (
            <React.Fragment key={index}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  backgroundColor: errorRows.includes(index) ? "#fff5f5" : undefined,
                  border: errorRows.includes(index) ? "1px solid #d32f2f" : "1px solid #d1d9e0",
                }}
              >
                <Box
                  sx={{
                    padding: "12px 10px",
                    borderRight: "1px solid #d1d9e0",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {row.fromProduct ? (
                    <Box sx={{ width: "100%", pr: 1 }}>
                      <ProductRow
                        product={row.fromProduct}
                        isAdded={true}
                        onClick={() => handleInputChange(index, "from", "", undefined)}
                      />
                    </Box>
                  ) : row.fromValue ? (
                    renderSKUOnly(row.fromValue, () => handleInputChange(index, "from", "", undefined))
                  ) : (
                    <ProductSearchField
                      value={row.fromValue}
                      index={index}
                      country={fromCountry}
                      handleInputChange={(i, sku, product) => handleInputChange(i, "from", sku, product)}
                      placeholder="Enter SKU, PLU, product name, etc."
                    />
                  )}
                </Box>

                <Box
                  sx={{
                    padding: "12px 10px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {row.toProduct ? (
                    <Box sx={{ width: "100%" }}>
                      <ProductRow
                        product={row.toProduct}
                        isAdded={true}
                        onClick={() => handleInputChange(index, "to", "", undefined)}
                      />
                    </Box>
                  ) : row.toValue ? (
                    renderSKUOnly(row.toValue, () => handleInputChange(index, "to", "", undefined))
                  ) : (
                    <Box sx={{ width: "100%" }}>
                      <ProductSearchField
                        value={row.toValue}
                        country={toCountry}
                        index={index}
                        handleInputChange={(i, sku, product) => handleInputChange(i, "to", sku, product)}
                        placeholder="Enter SKU, PLU, product name, etc."
                      />
                    </Box>
                  )}
                </Box>
              </Box>
              {errorRows.includes(index) && (
                <Box sx={{ padding: "4px 16px", color: "#d32f2f", fontSize: "0.8rem" }}>
                  Both products must be selected to sync.
                </Box>
              )}
            </React.Fragment>
          ))}
          <Box sx={{ padding: "12px 16px", border: "1px solid #d1d9e0" }}>
            <Button type="link" onClick={handleAddRow} style={{ textDecoration: "none" }}>
              <Add style={{ marginRight: 8 }} />
              <span style={{ fontWeight: 600 }}>Add</span>
            </Button>
          </Box>
        </Box>
      </Grid>

      {isSameLocationSync && (
        <SyncOptionsSelect selectedOptions={selectedSyncOptions} setSelectedOptions={setSelectedSyncOptions} />
      )}

      <Grid item xs={12}>
        <Grid container justifyContent="space-between" alignItems="center" marginTop={4}>
          <Grid item>
            <Typography
              onClick={handleCancel}
              sx={{
                color: "#666",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              Reset
            </Typography>
          </Grid>

          <Grid item xs={6}>
            {globalError && (
              <Box
                sx={{
                  backgroundColor: "#fff5f5",
                  border: "1px solid #d32f2f",
                  borderRadius: "4px",
                  padding: "12px 16px",
                  color: "#d32f2f",
                  fontSize: "0.9rem",
                  textAlign: "center",
                  margin: "0 auto 16px auto",
                }}
              >
                {globalError}
              </Box>
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button type="primary" label="Sync SKU" onClick={handleSync} />
            </Box>
          </Grid>
        </Grid>
      </Grid>
      <Dialog open={open} onClose={handleClose} PaperProps={{ sx: { minWidth: 400, textAlign: "left" } }}>
        <DialogContent>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Sync initiated successfully
          </Typography>
          <Typography variant="body2" fontWeight={700} gutterBottom>
            Please note:
            <span style={{ fontWeight: 400 }}>
              {" "}
              This process may take some time to complete. You will receive an email notification once the sync is
              completed.
            </span>
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <MuiButton
              variant="contained"
              onClick={handleClose}
              sx={{
                background: "#000",
                color: "#fff",
                borderRadius: 0,
                px: 4,
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": { background: "#222" },
              }}
            >
              OK
            </MuiButton>
          </Box>
        </DialogContent>
      </Dialog>

      <MuiDialog open={confirmSyncOpen} onClose={() => setConfirmSyncOpen(false)}>
        <DialogTitle>Proceed With Sync?</DialogTitle>
        <DialogContent>
          <Typography>
            <b>This action is final and cannot be undone</b>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button type="secondary" onClick={() => setConfirmSyncOpen(false)}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleConfirmSync}>
            Proceed
          </Button>
        </DialogActions>
      </MuiDialog>
    </Grid>
  );
};
